import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Plus, 
  Upload, 
  Trash2, 
  Users, 
  Phone, 
  Mail, 
  MessageSquare,
  Search,
  FileSpreadsheet
} from 'lucide-react';

export default function ContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  const [newContact, setNewContact] = useState({
    name: '',
    phone: '',
    email: '',
    whatsapp: '',
    tags: ''
  });

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await api.get('/contacts');
      setContacts(response.data);
    } catch (error) {
      toast.error('Kişiler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    try {
      await api.post('/contacts', {
        ...newContact,
        tags: newContact.tags.split(',').map(t => t.trim()).filter(Boolean)
      });
      toast.success('Kişi eklendi');
      setIsAddDialogOpen(false);
      setNewContact({ name: '', phone: '', email: '', whatsapp: '', tags: '' });
      fetchContacts();
    } catch (error) {
      toast.error('Kişi eklenemedi');
    }
  };

  const handleDeleteContact = async (id) => {
    if (!window.confirm('Bu kişiyi silmek istediğinize emin misiniz?')) return;
    
    try {
      await api.delete(`/contacts/${id}`);
      toast.success('Kişi silindi');
      fetchContacts();
    } catch (error) {
      toast.error('Kişi silinemedi');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/contacts/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${response.data.count} kişi yüklendi`);
      setIsUploadDialogOpen(false);
      fetchContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Dosya yüklenemedi');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const filteredContacts = contacts.filter(contact =>
    contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contact.phone.includes(searchTerm) ||
    (contact.email && contact.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="contacts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Kişiler</h1>
          <p className="text-muted-foreground">Kampanyalarınız için kişi listesi yönetimi</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="upload-contacts-btn">
                <Upload className="w-4 h-4 mr-2" />
                CSV/Excel Yükle
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Kişileri İçe Aktar</DialogTitle>
                <DialogDescription>
                  CSV veya Excel dosyası yükleyerek toplu kişi ekleyin.
                  Dosyanızda <strong>name</strong> ve <strong>phone</strong> sütunları zorunludur.
                  Opsiyonel: email, whatsapp, tags
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                  <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="file-upload"
                    data-testid="file-upload-input"
                  />
                  <label 
                    htmlFor="file-upload"
                    className="cursor-pointer"
                  >
                    <Button variant="outline" disabled={uploading} asChild>
                      <span>
                        {uploading ? 'Yükleniyor...' : 'Dosya Seç'}
                      </span>
                    </Button>
                  </label>
                  <p className="text-sm text-muted-foreground mt-2">
                    .csv, .xlsx, .xls dosyaları desteklenir
                  </p>
                </div>
                
                <div className="bg-secondary/50 rounded-lg p-4">
                  <p className="text-sm font-medium mb-2">Örnek CSV formatı:</p>
                  <code className="text-xs text-muted-foreground font-mono">
                    name,phone,email,whatsapp,tags<br/>
                    Ali Yılmaz,+905551234567,ali@email.com,+905551234567,müşteri
                  </code>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-contact-btn">
                <Plus className="w-4 h-4 mr-2" />
                Kişi Ekle
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Yeni Kişi Ekle</DialogTitle>
                <DialogDescription>
                  Kampanyalarınızda kullanmak için yeni bir kişi ekleyin.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddContact}>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Ad Soyad *</Label>
                    <Input
                      id="name"
                      value={newContact.name}
                      onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                      placeholder="Örn: Ali Yılmaz"
                      required
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefon *</Label>
                    <Input
                      id="phone"
                      value={newContact.phone}
                      onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                      placeholder="+905551234567"
                      required
                      data-testid="contact-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">E-posta</Label>
                    <Input
                      id="email"
                      type="email"
                      value={newContact.email}
                      onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                      placeholder="ornek@email.com"
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="whatsapp">WhatsApp</Label>
                    <Input
                      id="whatsapp"
                      value={newContact.whatsapp}
                      onChange={(e) => setNewContact({ ...newContact, whatsapp: e.target.value })}
                      placeholder="+905551234567 (boş bırakılırsa telefon kullanılır)"
                      data-testid="contact-whatsapp-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tags">Etiketler</Label>
                    <Input
                      id="tags"
                      value={newContact.tags}
                      onChange={(e) => setNewContact({ ...newContact, tags: e.target.value })}
                      placeholder="müşteri, vip (virgülle ayırın)"
                      data-testid="contact-tags-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                    İptal
                  </Button>
                  <Button type="submit" data-testid="save-contact-btn">Kaydet</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search & Stats */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Kişi ara (isim, telefon, email)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-contacts-input"
          />
        </div>
        <Badge variant="secondary" className="py-2 px-4">
          <Users className="w-4 h-4 mr-2" />
          {filteredContacts.length} kişi
        </Badge>
      </div>

      {/* Contacts Table */}
      <Card>
        <CardContent className="p-0">
          {filteredContacts.length === 0 ? (
            <div className="p-8 text-center">
              <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-medium mb-1">
                {searchTerm ? 'Kişi bulunamadı' : 'Henüz kişi yok'}
              </h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm ? 'Farklı bir arama terimi deneyin' : 'CSV yükleyerek veya manuel olarak kişi ekleyin'}
              </p>
              {!searchTerm && (
                <Button onClick={() => setIsAddDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  İlk Kişiyi Ekle
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ad Soyad</TableHead>
                  <TableHead>Telefon</TableHead>
                  <TableHead>E-posta</TableHead>
                  <TableHead>WhatsApp</TableHead>
                  <TableHead>Etiketler</TableHead>
                  <TableHead className="text-right">İşlem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredContacts.map((contact, index) => (
                  <TableRow 
                    key={contact.id} 
                    className="stagger-item"
                    style={{ animationDelay: `${index * 0.05}s` }}
                    data-testid={`contact-row-${contact.id}`}
                  >
                    <TableCell className="font-medium">{contact.name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        {contact.phone}
                      </div>
                    </TableCell>
                    <TableCell>
                      {contact.email ? (
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-muted-foreground" />
                          {contact.email}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {contact.whatsapp ? (
                        <div className="flex items-center gap-2">
                          <MessageSquare className="w-4 h-4 text-muted-foreground" />
                          {contact.whatsapp}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {contact.tags?.length > 0 ? (
                          contact.tags.map((tag, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteContact(contact.id)}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        data-testid={`delete-contact-${contact.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
